import { useQuery } from '@apollo/client';
import { Campaign } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { AnnouncementCard } from '~/components/announcements/AnnouncementCard';
import { useMarkAnnouncementsRead } from '~/components/announcements/mark-read';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { Toolbar } from '~/components/toolbar';
import { AnnouncementShowDocument } from './queries/show.generated';

export const AnnouncementDetail = () => {
  const { t } = useTranslation();
  const params = useParams<{ announcementId: string }>();
  const announcementId = Number(params.announcementId);

  const { data, loading } = useQuery(AnnouncementShowDocument, { variables: { announcementId } });
  const announcement = data?.announcement;

  const [markRead] = useMarkAnnouncementsRead();

  // Also marked here rather than only on the card click, so a shared or bookmarked link
  // counts as having read it.
  useEffect(() => {
    if (!announcement || announcement.read) return;

    void markRead({ variables: { input: { announcementIds: [announcement.id], read: true } } });
  }, [announcement, markRead]);

  if (!loading && !announcement) return <Navigate replace to="/not-found" />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <Toolbar
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<Campaign />}
          titleText={announcement?.title ?? t('components.announcements.title')}
        />
        <PageContainer>
          {announcement && (
            // Capped well short of the page width: long prose is hard to read across a full
            // monitor, and release notes are prose.
            <Box sx={{ maxWidth: 760, mx: 'auto', my: 3 }}>
              <AnnouncementCard announcement={announcement} />
            </Box>
          )}
        </PageContainer>
      </>
    </LoadingPane>
  );
};
